import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ScientificObjectRef } from '../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../scientific-objects/ScientificObjectSummary';
import { getScientificObjectSummary } from '../../scientific-objects/scientificObjectApi';
import { scientificObjectTypePath } from '../../scientific-objects/ScientificObjectType';
import { getRelationships, type RelationshipDirection } from './relationshipApi';
import type { ScientificRelationship } from './ScientificRelationship';
import type { Evidence } from '../evidence/Evidence';
import { getRelationshipEvidence } from '../evidence/evidenceApi';
import type { RelationshipType } from './ScientificRelationship';
import { relationshipHeading } from './relationshipPresentation';

interface RelationshipListProps {
  readonly reference: ScientificObjectRef;
  readonly direction: RelationshipDirection;
  readonly relationshipType: RelationshipType;
}

type RelationshipListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly RelationshipItem[] }
  | { readonly status: 'error'; readonly message: string };

interface RelationshipItem {
  readonly relationship: ScientificRelationship;
  readonly summary: ScientificObjectSummary;
  readonly evidence: readonly Evidence[];
}

export function RelationshipList({ reference, direction, relationshipType }: RelationshipListProps) {
  const [state, setState] = useState<RelationshipListState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    getRelationships(direction, reference)
      .then(async (relationships) => {
        const matching = relationships.filter((relationship) => relationship.relationshipType === relationshipType);
        const items = await Promise.all(
          matching.map(async (relationship) => {
            const relatedRef = direction === 'outgoing' ? relationship.object : relationship.subject;
            const [summary, evidence] = await Promise.all([
              getScientificObjectSummary(relatedRef),
              getRelationshipEvidence(relationship.id)
            ]);
            return { relationship, summary, evidence };
          })
        );
        if (active) setState({ status: 'ready', items });
      })
      .catch((error: unknown) => {
        if (active)
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Unable to load relationships.'
          });
      });
    return () => {
      active = false;
    };
  }, [direction, reference.id, reference.type, relationshipType]);

  const heading = relationshipHeading(relationshipType, direction);
  const headingId = `${direction}-${relationshipType.toLowerCase()}-relationships-heading`;

  return (
    <section className="relationship-list" aria-labelledby={headingId}>
      <h2 id={headingId}>{heading}</h2>
      {state.status === 'loading' ? <p>Loading relationships…</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' && state.items.length === 0 ? <p>None recorded.</p> : null}
      {state.status === 'ready' && state.items.length > 0 ? (
        <ul>
          {state.items.map(({ relationship, summary, evidence }) => (
            <li key={relationship.id}>
              <Link
                className="relationship-object-link"
                to={`/objects/${scientificObjectTypePath(summary.reference.type)}/${summary.reference.id}`}
              >
                {summary.displayName}
              </Link>
              <EvidenceList evidence={evidence} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function EvidenceList({ evidence }: { readonly evidence: readonly Evidence[] }) {
  if (evidence.length === 0) return <p className="relationship-evidence-empty">No evidence recorded.</p>;
  return (
    <div className="relationship-evidence">
      <h3>Evidence</h3>
      {evidence.map((item) => (
        <article key={item.id}>
          <p>
            <strong>{item.sourceReference.sourceSystem}:</strong> {item.sourceReference.sourceRecordId}
          </p>
          {item.sourceReference.displayLabel ? <p>{item.sourceReference.displayLabel}</p> : null}
          <details>
            <summary>Source provenance</summary>
            <dl>
              <div>
                <dt>Recorded as</dt>
                <dd>{item.provenance.method === 'IMPORTED' ? 'Imported' : 'Manual entry'}</dd>
              </div>
              <div>
                <dt>Origin</dt>
                <dd>{item.provenance.originSystem}</dd>
              </div>
              {item.provenance.sourceVersion ? (
                <div>
                  <dt>Source version</dt>
                  <dd>{item.provenance.sourceVersion}</dd>
                </div>
              ) : null}
              <div>
                <dt>Recorded</dt>
                <dd>{new Date(item.provenance.recordedAt).toLocaleString()}</dd>
              </div>
            </dl>
          </details>
        </article>
      ))}
    </div>
  );
}
